import type { AgentChatEntry } from '@/_contract/agentChat';
import type { createAgentChatController } from './agentChatController';
import type { createMicrophoneTestController } from './microphoneTestController';
import type { createForgeSpeechPlayer } from './forgeSpeechPlayer';

type SpeechState = { supported: boolean; autoSpeak: boolean; phase: 'idle' | 'speaking'; backend?: 'browser' | 'forge'; error?: string };
type SpeechBrowser = Pick<Window & typeof globalThis, 'speechSynthesis' | 'SpeechSynthesisUtterance'>;

export function spokenReply(text: string) {
    return text
        .replace(/^\s*(`{3,}|~{3,}).*$(?:[\s\S]*?)^\s*\1\s*$/gm, ' ')
        .replace(/^\s*(`{3,}|~{3,})[\s\S]*$/gm, ' ')
        .replace(/`[^`]*`/g, ' ')
        .replace(/^[ \t]*\|?([^\n]*\|[^\n]*)\n[ \t]*\|?[ \t]*:?-{3,}:?[ \t]*(?:\|[ \t]*:?-{3,}:?[ \t]*)+\|?[ \t]*\n((?:[^\n]*\|[^\n]*(?:\n|$))+)/gm,
            (_table, _header: string, rows: string) => rows.trim().split('\n').map(row => {
                const cells = row.trim().replace(/^\||\|$/g, '').split(/(?<!\\)\|/).map(cell => cell.trim().replace(/\\\|/g, '|'));
                return `${cells.join(': ').replace(/[.!?]$/, '')}.\n`;
            }).join(''))
        .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
        .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
        .replace(/https?:\/\/\S+/g, ' ')
        .replace(/^[ \t]*(?:#{1,6}\s+|>\s*|[-*+]\s+|\d+\.\s+)/gm, '')
        .replace(/[*_~|]/g, '')
        .replace(/\s+/g, ' ').trim();
}

export function createSpeechPlaybackController(
    chat: ReturnType<typeof createAgentChatController>,
    microphone: ReturnType<typeof createMicrophoneTestController>,
    browser: () => SpeechBrowser = () => window,
    timeoutMs = 45000,
    forge?: ReturnType<typeof createForgeSpeechPlayer>,
) {
    let state: SpeechState = { supported: false, autoSpeak: true, phase: 'idle' };
    const listeners = new Set<() => void>();
    let generation = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let utterance: SpeechSynthesisUtterance | undefined;
    let threadId: string | undefined;
    let baseline: Set<string> | undefined;
    let creating = false;
    const update = (patch: Partial<SpeechState>) => {
        state = { ...state, ...patch };
        listeners.forEach(listener => listener());
    };
    const stop = () => {
        generation++;
        clearTimeout(timer);
        forge?.stop();
        if (utterance) {
            utterance.onend = null;
            utterance.onerror = null;
            browser().speechSynthesis.cancel();
            utterance = undefined;
        }
        update({ phase: 'idle' });
    };
    const fail = (message: string) => {
        stop();
        console.error('[agent-speech]', message);
        update({ error: message, autoSpeak: false });
    };
    const blocked = () => !!chat.getSnapshot().voiceBusy || microphone.getSnapshot().phase !== 'idle';
    const speak = (text: string) => {
        stop();
        if (!state.supported) { fail('Speech playback is unavailable in this browser. Text replies remain available.'); return; }
        if (blocked()) { fail('Release the microphone before reading a reply aloud.'); return; }
        const prose = spokenReply(text);
        if (!prose) { fail('This reply has no prose to read aloud. Code and links are skipped.'); return; }
        const voices = state.backend === 'forge' ? [] : browser().speechSynthesis.getVoices().filter(voice => voice.localService);
        const voice = voices.find(voice => voice.default) ?? voices[0];
        if (state.backend !== 'forge' && !voice) { fail('No local browser voice is available. Remote speech services are not used.'); return; }
        const chunks = prose.match(/.{1,240}(?:\s|$)|\S{1,240}/g) ?? [];
        const current = generation;
        let index = 0;
        update({ phase: 'speaking', error: undefined });
        const next = () => {
            if (current !== generation) return;
            clearTimeout(timer);
            if (index >= chunks.length) { utterance = undefined; update({ phase: 'idle' }); return; }
            if (state.backend === 'forge' && forge) {
                timer = setTimeout(() => {
                    if (current === generation) fail('Forge speech playback timed out. Select Read aloud to retry.');
                }, 100000);
                void forge.play(chunks[index++]!, next, message => { if (current === generation) fail(message); });
                return;
            }
            try {
                const host = browser();
                utterance = new host.SpeechSynthesisUtterance(chunks[index++]);
                utterance.voice = voice;
                utterance.lang = voice!.lang;
                utterance.onend = next;
                utterance.onerror = event => {
                    if (current === generation) fail(`Speech playback failed (${event.error}). Select Read aloud to retry.`);
                };
                timer = setTimeout(() => {
                    if (current === generation) fail('Speech playback did not finish. Select Read aloud to retry; this browser may require a direct tap.');
                }, timeoutMs);
                host.speechSynthesis.speak(utterance);
            } catch (error) {
                fail(`Speech playback failed: ${error instanceof Error ? error.message : String(error)}`);
            }
        };
        next();
    };
    const unlock = () => {
        if (state.backend === 'forge' && forge) {
            const current = generation;
            void forge.unlock().catch(error => {
                if (current === generation) fail(error instanceof Error ? error.message : 'Audio could not be enabled.');
            });
        }
    };
    const replyText = (entry: AgentChatEntry) => chat.getSnapshot().view?.transport === 'ahp'
        ? entry.speechContent ?? '' : entry.content;
    const entryKey = (entry: AgentChatEntry, index: number) => JSON.stringify([index, entry.at, replyText(entry)]);
    const observe = () => {
        const snapshot = chat.getSnapshot();
        if (snapshot.threadId !== threadId) {
            const newConversation = !threadId && creating;
            stop();
            threadId = snapshot.threadId;
            baseline = newConversation ? new Set() : undefined;
            creating = false;
        }
        if (!snapshot.threadId && snapshot.sending) creating = true;
        if (snapshot.loading || snapshot.voiceBusy || snapshot.sending) stop();
        const view = snapshot.view;
        if (!view || snapshot.loading) return;
        const keys = view.entries.map(entryKey);
        if (!baseline) {
            // Restored history is never narrated; a turn already in progress can finish aloud.
            const lastUser = view.entries.findLastIndex(entry => entry.role === 'user');
            baseline = new Set(view.status === 'idle' ? keys : keys.slice(0, lastUser + 1));
        }
        if (view.status !== 'idle' || snapshot.sending || snapshot.reconnecting || snapshot.error) return;
        const replies = view.entries.filter((entry, index) => entry.role === 'agent' && !baseline?.has(keys[index]!));
        baseline = new Set(keys);
        if (state.autoSpeak && !blocked()) {
            const prose = replies.map(entry => spokenReply(replyText(entry))).filter(Boolean).join('\n');
            if (prose) speak(prose);
        }
    };
    return {
        getSnapshot: () => state,
        subscribe(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; },
        start() {
            const host = browser();
            const active = new AbortController();
            const supported = !!host.speechSynthesis && typeof host.SpeechSynthesisUtterance === 'function';
            const checkVoices = () => {
                if (state.backend === 'forge') return;
                const available = supported && host.speechSynthesis.getVoices().some(voice => voice.localService);
                update({ supported: available, backend: 'browser', error: available ? undefined : supported
                    ? 'No local browser voice is available yet. Remote speech services are not used.'
                    : 'Speech playback is unavailable in this browser. Text replies remain available.' });
            };
            checkVoices();
            if (forge) void forge.configure(active.signal).then(enabled => {
                if (!active.signal.aborted) {
                    if (enabled) update({ supported: true, backend: 'forge', error: undefined });
                    else checkVoices();
                }
            }).catch(error => {
                if (!active.signal.aborted) {
                    fail(error instanceof Error ? error.message : 'Speech configuration failed.');
                    update({ supported: false });
                }
            });
            if (supported) host.speechSynthesis.addEventListener('voiceschanged', checkVoices);
            observe();
            const offChat = chat.subscribe(observe);
            const offMic = microphone.subscribe(() => { if (blocked()) stop(); });
            return () => {
                active.abort(); offChat(); offMic();
                if (supported) host.speechSynthesis.removeEventListener('voiceschanged', checkVoices);
                stop(); forge?.dispose(); baseline = undefined; threadId = undefined; creating = false;
            };
        },
        stop,
        setAutoSpeak(enabled: boolean) {
            if (!state.supported) return;
            if (!enabled) stop();
            update({ autoSpeak: enabled, error: undefined });
            if (enabled) unlock();
        },
        readAloud() {
            const snapshot = chat.getSnapshot();
            if (!snapshot.view || snapshot.view.status !== 'idle' || snapshot.loading || snapshot.sending || snapshot.reconnecting) {
                fail('Wait for a completed reply before reading aloud.'); return;
            }
            const latest = snapshot.view.entries.findLast(entry => entry.role === 'agent');
            if (!latest) { fail('There is no assistant reply to read aloud.'); return; }
            speak(replyText(latest));
            unlock();
        },
    };
}
